import { useMemo, useState, type JSX } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isHTTPError } from 'ky';
import {
  type CreateTrialVisitWindowModeRequest,
  type TrialVisitWindowModeDto,
  type UpdateTrialVisitWindowModeRequest,
} from '@ctms/contracts';
import { AlertCircle, X } from 'lucide-react';
import { z } from 'zod';

import { getApiErrorMessage } from '../../../api/client.js';
import { trialsApi } from '../trials.api.js';
import { trialQueryKeys } from '../trials.query-keys.js';
import {
  WINDOW_RANGE_OPTIONS,
  type WindowModeLang,
  type WindowModeMessages,
  windowModeTexts,
} from './window-modes.i18n.js';

const fieldClass =
  'mt-1.5 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:border-cyan-500 focus:outline-none disabled:opacity-50';

const labelClass = 'block text-xs font-medium uppercase tracking-wider text-slate-300';

const RANGE_STRINGS = WINDOW_RANGE_OPTIONS.map(String);

function queryRetry(count: number, err: unknown): boolean {
  return isHTTPError(err) ? false : count < 3;
}

/** Signed whole-day count, mirroring `parseDayCount` without a cross-file import. */
export function toDay(value: string): number | undefined {
  const trimmed = value.trim();
  if (!/^-?\d+$/.test(trimmed)) {
    return undefined;
  }
  const parsed = Number(trimmed);
  if (!Number.isSafeInteger(parsed)) {
    return undefined;
  }
  return parsed;
}

/** D2 (#36): UI collects MID ± X; the API receives the signed symmetric range. */
export function buildWindowModeRange(
  mid: number,
  x: number,
): { minDays: number; optDays: number; maxDays: number } {
  return { minDays: mid - x, optDays: mid, maxDays: mid + x };
}

/** Symmetric ± offset of a window mode, or `null` for an out-of-shape range. */
function symmetricOffset(mode: TrialVisitWindowModeDto): number | null {
  if (mode.minDays === null || mode.optDays === null || mode.maxDays === null) {
    return null;
  }
  const left = mode.optDays - mode.minDays;
  const right = mode.maxDays - mode.optDays;
  return left === right ? left : null;
}

export function formatWindowMode(mode: TrialVisitWindowModeDto, lang: WindowModeLang): string {
  const messages = windowModeTexts(lang);
  if (mode.minDays === null && mode.optDays === null && mode.maxDays === null) {
    return messages.noRange;
  }
  const x = symmetricOffset(mode);
  if (x !== null) {
    return lang === 'en' ? `Day ${mode.optDays} ± ${x}` : `Dzień ${mode.optDays} ± ${x}`;
  }
  return `min ${mode.minDays} / opt ${mode.optDays} / max ${mode.maxDays}`;
}

export interface WindowModeFormInput {
  label: string;
  hasRange: boolean;
  mid: string;
  range: string;
  isDefault: boolean;
}

export function buildWindowModeSchema(
  otherDefaultCount: number,
  messages: WindowModeMessages,
) {
  return z
    .object({
      label: z.string().trim().min(1, messages.labelRequired).max(255, messages.labelRequired),
      hasRange: z.boolean(),
      mid: z.string(),
      range: z.string(),
      isDefault: z.boolean(),
    })
    .superRefine((value, ctx) => {
      if (value.hasRange) {
        if (toDay(value.mid) === undefined) {
          ctx.addIssue({ code: 'custom', path: ['mid'], message: messages.midInvalid });
        }
        if (!RANGE_STRINGS.includes(value.range)) {
          ctx.addIssue({ code: 'custom', path: ['range'], message: messages.rangeInvalid });
        }
      }
      // D5 (#36): after this mutation the list holds exactly one default mode.
      const defaultCount = otherDefaultCount + (value.isDefault ? 1 : 0);
      if (defaultCount !== 1) {
        ctx.addIssue({ code: 'custom', path: ['isDefault'], message: messages.exactlyOneDefault });
      }
    });
}

function prefill(
  mode: TrialVisitWindowModeDto | null,
  modes: readonly TrialVisitWindowModeDto[],
): WindowModeFormInput {
  if (mode) {
    const hasRange = mode.minDays !== null && mode.optDays !== null && mode.maxDays !== null;
    return {
      label: mode.label,
      hasRange,
      mid: hasRange ? String(mode.optDays) : '',
      range: hasRange ? String((mode.optDays as number) - (mode.minDays as number)) : '',
      isDefault: mode.isDefault,
    };
  }
  return {
    label: '',
    hasRange: true,
    mid: '',
    range: '',
    isDefault: modes.filter((row) => row.isDefault).length === 0,
  };
}

export function toCreateWindowModeRequest(
  trialId: number,
  visitId: number,
  data: WindowModeFormInput,
): CreateTrialVisitWindowModeRequest {
  const request: CreateTrialVisitWindowModeRequest = {
    trialId,
    trialVisitId: visitId,
    label: data.label.trim(),
    isDefault: data.isDefault,
  };
  if (data.hasRange) {
    const mid = toDay(data.mid);
    if (mid === undefined) {
      throw new Error('MID is required');
    }
    return { ...request, ...buildWindowModeRange(mid, Number(data.range)) };
  }
  // Create contract: absent (all undefined) = mode without a range.
  return request;
}

export function toUpdateWindowModeRequest(data: WindowModeFormInput): UpdateTrialVisitWindowModeRequest {
  const request: UpdateTrialVisitWindowModeRequest = {
    label: data.label.trim(),
    isDefault: data.isDefault,
  };
  if (data.hasRange) {
    const mid = toDay(data.mid);
    if (mid === undefined) {
      throw new Error('MID is required');
    }
    return { ...request, ...buildWindowModeRange(mid, Number(data.range)) };
  }
  // Update contract: explicit null clears the stored range.
  return { ...request, minDays: null, optDays: null, maxDays: null };
}

export interface WindowModesDialogProps {
  trialId: number;
  visitId: number;
  visitName: string;
  canWrite: boolean;
  lang?: WindowModeLang;
  onClose: () => void;
}

interface ModeFormProps {
  trialId: number;
  visitId: number;
  mode: TrialVisitWindowModeDto | null;
  modes: readonly TrialVisitWindowModeDto[];
  messages: WindowModeMessages;
  onCancel: () => void;
  onSaved: () => void;
}

function ModeForm({
  trialId,
  visitId,
  mode,
  modes,
  messages,
  onCancel,
  onSaved,
}: ModeFormProps): JSX.Element {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const otherDefaultCount = useMemo(
    () => modes.filter((row) => row.id !== mode?.id && row.isDefault).length,
    [modes, mode],
  );
  const schema = useMemo(
    () => buildWindowModeSchema(otherDefaultCount, messages),
    [otherDefaultCount, messages],
  );

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<WindowModeFormInput>({
    resolver: zodResolver(schema),
    defaultValues: prefill(mode, modes),
  });

  const hasRange = watch('hasRange');

  const onSubmit = async (data: WindowModeFormInput): Promise<void> => {
    setErrorMessage(null);
    setIsSubmitting(true);
    try {
      if (mode) {
        await trialsApi.updateWindowMode(trialId, visitId, mode.id, toUpdateWindowModeRequest(data));
      } else {
        await trialsApi.createWindowMode(trialId, visitId, toCreateWindowModeRequest(trialId, visitId, data));
      }
      onSaved();
    } catch (err: unknown) {
      setErrorMessage(await getApiErrorMessage(err, messages.unknownError));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="mt-4 space-y-4 rounded-lg border border-slate-800 p-4" noValidate>
      {errorMessage && (
        <div role="alert" className="flex items-center gap-2 rounded-md border border-rose-800/60 bg-rose-950/40 p-3 text-sm text-rose-300">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{errorMessage}</span>
        </div>
      )}

      <div>
        <label htmlFor="window-mode-label" className={labelClass}>{messages.label}</label>
        <input
          id="window-mode-label"
          type="text"
          disabled={isSubmitting}
          className={fieldClass}
          placeholder={messages.labelPlaceholder}
          {...register('label')}
        />
        {errors.label && <p className="mt-1 text-xs text-rose-400">{errors.label.message}</p>}
      </div>

      <label className="flex items-center gap-2 text-sm text-slate-200">
        <input type="checkbox" disabled={isSubmitting} {...register('hasRange')} />
        {messages.hasRange}
      </label>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="window-mode-mid" className={labelClass}>{messages.mid}</label>
          <input
            id="window-mode-mid"
            type="text"
            inputMode="numeric"
            disabled={isSubmitting || !hasRange}
            className={fieldClass}
            placeholder="0"
            {...register('mid')}
          />
          {errors.mid && <p className="mt-1 text-xs text-rose-400">{errors.mid.message}</p>}
        </div>
        <div>
          <label htmlFor="window-mode-range" className={labelClass}>{messages.range}</label>
          <select id="window-mode-range" disabled={isSubmitting || !hasRange} className={fieldClass} {...register('range')}>
            <option value="">{messages.rangePick}</option>
            {WINDOW_RANGE_OPTIONS.map((value) => (
              <option key={value} value={String(value)}>
                ±{value}
              </option>
            ))}
          </select>
          {errors.range && <p className="mt-1 text-xs text-rose-400">{errors.range.message}</p>}
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm text-slate-200">
        <input type="checkbox" disabled={isSubmitting} {...register('isDefault')} />
        {messages.defaultMode}
      </label>
      {errors.isDefault && <p className="mt-1 text-xs text-rose-400">{errors.isDefault.message}</p>}

      <div className="flex justify-end gap-2 border-t border-slate-800 pt-4">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-800"
        >
          {messages.cancel}
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="rounded-md bg-cyan-600 px-3.5 py-1.5 text-xs font-medium text-white hover:bg-cyan-500 disabled:opacity-50"
        >
          {isSubmitting ? messages.saving : messages.save}
        </button>
      </div>
    </form>
  );
}

export function WindowModesDialog({
  trialId,
  visitId,
  visitName,
  canWrite,
  lang = 'pl',
  onClose,
}: WindowModesDialogProps): JSX.Element {
  const messages = windowModeTexts(lang);
  const queryClient = useQueryClient();
  const [editingMode, setEditingMode] = useState<TrialVisitWindowModeDto | null>(null);
  const [formOpen, setFormOpen] = useState(false);

  const modesQuery = useQuery({
    queryKey: trialQueryKeys.windowModes(trialId, visitId),
    queryFn: () => trialsApi.listWindowModes(trialId, visitId),
    staleTime: 5 * 60 * 1000,
    retry: queryRetry,
  });

  const modes = modesQuery.data ?? [];

  const handleSaved = (): void => {
    void queryClient.invalidateQueries({ queryKey: trialQueryKeys.windowModes(trialId, visitId) });
    setEditingMode(null);
    setFormOpen(false);
  };

  const startAdd = (): void => {
    setEditingMode(null);
    setFormOpen(true);
  };

  const startEdit = (mode: TrialVisitWindowModeDto): void => {
    setEditingMode(mode);
    setFormOpen(true);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="window-modes-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4"
    >
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl border border-slate-800 bg-slate-900 p-6 text-slate-100 shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-800 pb-4">
          <div>
            <h2 id="window-modes-title" className="text-base font-semibold text-slate-100">
              {messages.dialogTitle} — {visitName}
            </h2>
            <p className="mt-1 text-xs text-slate-400">{messages.dialogSubtitle}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={messages.close}
            className="rounded-md p-1.5 text-slate-400 hover:bg-slate-800 hover:text-slate-200"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {modesQuery.isLoading ? (
          <p className="py-6 text-sm text-slate-400">…</p>
        ) : modesQuery.isError ? (
          <div role="alert" className="py-6 text-sm text-rose-300">
            {messages.unknownError}
          </div>
        ) : modes.length === 0 && !formOpen ? (
          <p className="py-6 text-sm text-slate-400">{messages.noModes}</p>
        ) : (
          <ul className="mt-4 space-y-2">
            {modes.map((mode) => (
              <li
                key={mode.id}
                className="flex items-center justify-between gap-3 rounded-md border border-slate-800 px-3 py-2"
              >
                <div className="min-w-0">
                  <span className="text-sm text-slate-100">{mode.label}</span>
                  <span className="ml-2 text-xs text-slate-400">{formatWindowMode(mode, lang)}</span>
                  {mode.isDefault && (
                    <span className="ml-2 rounded bg-cyan-900/60 px-1.5 py-0.5 text-xs text-cyan-300">
                      {messages.defaultBadge}
                    </span>
                  )}
                </div>
                {canWrite && (
                  <button
                    type="button"
                    onClick={() => startEdit(mode)}
                    className="rounded-md border border-slate-700 px-2 py-1 text-xs text-slate-200 hover:bg-slate-800"
                  >
                    {messages.editMode}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        {formOpen && (
          <ModeForm
            trialId={trialId}
            visitId={visitId}
            mode={editingMode}
            modes={modes}
            messages={messages}
            onCancel={() => {
              setEditingMode(null);
              setFormOpen(false);
            }}
            onSaved={handleSaved}
          />
        )}

        {canWrite && !formOpen && (
          <button
            type="button"
            onClick={startAdd}
            className="mt-4 rounded-md bg-cyan-600 px-3.5 py-1.5 text-xs font-medium text-white hover:bg-cyan-500"
          >
            {messages.addMode}
          </button>
        )}
      </div>
    </div>
  );
}